"use client";

import { createPortal } from "react-dom";
import { useEffect, useState, type ReactNode } from "react";
import {
  MODAL_PANEL_ANIMATION_MS,
  MODAL_SHEET_ANIMATION_MS,
  modalDrawerPanelMotion,
  modalOverlayMotion,
  modalPanelMotion,
  modalResponsiveDrawerPanelMotion,
  modalSheetOverlayMotion,
  modalSheetPanelMotion,
} from "./modalMotion";
import { useModalPresence } from "./useModalPresence";

export type ModalAlign = "center" | "bottom" | "right" | "responsive-drawer";

export type ModalShellProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  panelClassName?: string;
  overlayClassName?: string;
  zIndexClass?: string;
  preventBackdropClose?: boolean;
  align?: ModalAlign;
  /** portal เข้า element นี้แทน body — ใช้ absolute inset-0 ภายใน host */
  scopedHost?: Element | null;
  /** ทำ panel นี้เป็น host สำหรับ scoped bottom sheet */
  hostPanel?: boolean;
  /** เรียกหลัง exit animation จบ (ก่อน unmount portal) */
  onExited?: () => void;
};

function shellAlignClass(align: ModalAlign): string {
  switch (align) {
    case "bottom":
      return "items-end";
    case "right":
      return "items-stretch justify-end";
    case "responsive-drawer":
      return "items-end min-[900px]:items-stretch min-[900px]:justify-end";
    default:
      return "items-center justify-center";
  }
}

function shellMotionKind(align: ModalAlign): "modal" | "sheet" | "drawer" {
  if (align === "bottom") return "sheet";
  if (align === "right" || align === "responsive-drawer") return "drawer";
  return "modal";
}

function shellPanelMotion(align: ModalAlign, phase: Parameters<typeof modalPanelMotion>[0]) {
  switch (align) {
    case "bottom":
      return modalSheetPanelMotion(phase);
    case "right":
      return modalDrawerPanelMotion(phase);
    case "responsive-drawer":
      return modalResponsiveDrawerPanelMotion(phase);
    default:
      return modalPanelMotion(phase);
  }
}

export function ModalShell({
  isOpen,
  onClose,
  children,
  panelClassName = "",
  overlayClassName,
  zIndexClass = "z-100",
  preventBackdropClose = false,
  align = "center",
  scopedHost = null,
  hostPanel = false,
  onExited,
}: ModalShellProps) {
  const [mounted, setMounted] = useState(false);
  const isSheet = align === "bottom";
  const isDrawer =
    align === "right" || align === "responsive-drawer";
  const motionDurationMs = isSheet || isDrawer
    ? MODAL_SHEET_ANIMATION_MS
    : MODAL_PANEL_ANIMATION_MS;
  const { rendered, phase, onPanelMotionEnd } = useModalPresence(
    isOpen,
    motionDurationMs,
    onExited,
    shellMotionKind(align),
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !rendered) return null;

  const alignClass = shellAlignClass(align);
  const containerPadding = isSheet || isDrawer ? "p-0" : "p-0 md:p-4";
  const overlayMotion =
    isSheet || isDrawer
      ? modalSheetOverlayMotion(phase)
      : modalOverlayMotion(phase);
  const panelMotion = shellPanelMotion(align, phase);
  const isScoped = scopedHost != null;
  const resolvedOverlayClassName =
    overlayClassName ??
    (isSheet
      ? isScoped
        ? "bg-aera-black/40 rounded-tl-3xl rounded-bl-3xl"
        : "bg-aera-black/40 rounded-t-3xl"
      : isDrawer
        ? "bg-aera-black/40"
        : "bg-aera-black/50 backdrop-blur-sm");
  const portalTarget = isScoped ? scopedHost : document.body;
  const shellPositionClass = isScoped
    ? "absolute inset-0 z-20"
    : `fixed inset-0 ${zIndexClass}`;

  return createPortal(
    <div
      className={`${shellPositionClass} flex ${alignClass} ${containerPadding}`}
      aria-hidden={phase !== "open" && phase !== "entering"}
    >
      <div
        className={`absolute inset-0 z-0 ${resolvedOverlayClassName} ${overlayMotion}`}
        onClick={preventBackdropClose ? undefined : onClose}
      />
      <div
        className={`relative z-10 w-full ${panelMotion} ${panelClassName}`}
        data-aera-modal-align={align}
        {...(hostPanel ? { "data-aera-modal-panel": "" } : {})}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={onPanelMotionEnd}
      >
        {children}
      </div>
    </div>,
    portalTarget,
  );
}
